import { toast } from "react-hot-toast";
import { useAsyncStateSupabaseCallback } from "../../../utils/hooks/use-async-state-callback";

import type { UUID } from "../../../utils/uuid";
import type { Level } from "../../program-access/access-model";

/** A contributor's level on the program after the move, in place of the one they keep. */
export interface LevelChange {
  person_id: UUID;
  level: Level;
}

/**
 * Moves a program or folder into a program, or to the top level with no parent, with a title, and
 * says if it failed. Resolves to whether it moved. Each contributor keeps their access to it, unless
 * a level change gives them another. It goes after the others in a new parent, unless an order gives
 * its place, as for an undo. Only an admin can move one.
 */
export function useMoveProgram(): {
  move: (
    id: UUID,
    title: string,
    parentId: UUID | null,
    changes: LevelChange[],
    order?: number,
  ) => Promise<boolean>;
  moving: boolean;
} {
  const { load, loading: moving } = useAsyncStateSupabaseCallback(
    async (
      { supabase },
      id: UUID,
      title: string,
      parentId: UUID | null,
      changes: LevelChange[],
      order?: number,
    ): Promise<boolean> => {
      const { error } = await supabase.rpc("move_program", {
        program_id_: id,
        title_: title,
        access_: changes.map(({ person_id, level }) => ({ person_id, level })),
        ...(parentId ? { parent_id_: parentId } : {}),
        ...(order === undefined ? {} : { order_: order }),
      });
      if (error) {
        toast.error(
          error.code === "23505"
            ? "Something there has that name already. Choose another name."
            : "Something went wrong moving the program. Please try again.",
        );
        return false;
      }
      return true;
    },
    [],
  );

  return { move: async (...args) => (await load(...args)) ?? false, moving };
}
